import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import {
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  RotateCcw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY_URL = "https://calendly.com/ghiowebservices/15min";

function ScoreRing({ label, value, measured, t, testid }) {
  return (
    <div className="flex flex-col items-center gap-2" data-testid={`score-${testid}`}>
      <div className="score-ring" style={{ "--p": value ?? 0 }}>
        <span>{value ?? "–"}</span>
      </div>
      <div className="eyebrow text-ink">{label}</div>
      <div data-testid={`score-${testid}-measured`} className={`text-[11px] ${measured ? "text-ink" : "text-navy italic"}`}>
        {measured ? t("audit.result.measured") : t("audit.result.not_measured")}
      </div>
    </div>
  );
}

const BookCall = ({ t }) => (
  <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="audit-result-cta" className="btn-editorial btn-editorial-primary">
    {t("audit.result.cta")} <ArrowRight size={16} />
  </a>
);

const num = (v, d, lang) => v.toLocaleString(lang === "en" ? "en-GB" : "nl-NL", { minimumFractionDigits: d, maximumFractionDigits: d });
// The scan runs as a background job; poll until it's done (max ~2,5 min).
const POLL_MS = 2500;
const POLL_MAX = 60;
async function pollAudit(jobId) {
  let fails = 0;
  for (let i = 0; i < POLL_MAX; i += 1) {
    await new Promise((r) => setTimeout(r, POLL_MS));
    let data;
    try {
      ({ data } = await axios.get(`${API}/audit/${jobId}`));
      fails = 0;
    } catch (err) {
      const s = err?.response?.status;
      if ((s && s < 500) || ++fails > 3) throw err;
      continue;
    }
    if (data.status === "done") return data.report;
    if (data.status === "error") throw { detail: data.detail };
  }
  throw { detail: null };
}

const fmtMs = (a, lang) => (a?.value != null ? `${num(a.value / 1000, 1, lang)} s` : null);

function SpeedLine({ m, lang }) {
  if (!m?.performance_source) return null;
  const parts = [
    fmtMs(m.lcp, lang) && `LCP ${fmtMs(m.lcp, lang)}`,
    m.cls?.value != null && `CLS ${num(m.cls.value, 2, lang)}`,
    m.tbt?.value != null && `TBT ${Math.round(m.tbt.value)} ms`,
    m.total_bytes?.value != null && `${num(Math.round(m.total_bytes.value / 1024), 0, lang)} KB`,
  ].filter(Boolean);
  return (
    <p data-testid="audit-speed-line" className="mt-6 text-xs text-ink">
      {lang === "en" ? "Speed measured by Google PageSpeed (mobile): " : "Snelheid gemeten door Google PageSpeed (mobiel): "}
      {parts.join(" · ")}
    </p>
  );
}

function SeverityIcon({ s }) {
  if (s === "critical") return <AlertTriangle size={18} className="text-navy" />;
  if (s === "warning") return <AlertCircle size={18} className="text-amber-700" />;
  return <CheckCircle2 size={18} className="text-green-dark" />;
}

export default function AuditTool() {
  const { t, lang } = useLanguage();
  const [url, setUrl] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [result, setResult] = useState(null);
  const resultRef = useRef(null);

  const steps = t("audit.loading_steps");
  const sev = t("audit.severity");

  useEffect(() => {
    if (!loading) return;
    setStepIdx(0);
    const id = setInterval(() => {
      setStepIdx((s) => (s < steps.length - 1 ? s + 1 : s));
    }, 1400);
    return () => clearInterval(id);
  }, [loading, steps.length]);

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const start = await axios.post(`${API}/audit`, {
        url: url.trim(),
        name: name || undefined,
        email: email || undefined,
        lang,
      });
      const report = await pollAudit(start.data.job_id);
      setResult(report);
      if (report.status === "ok") toast.success(t("audit.toast_complete"));
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    } catch (err) {
      console.error(err);
      const d = err?.response?.data;
      toast.error(d?.error?.message || (typeof d?.detail === "string" && d.detail) || err?.detail || t("audit.toast_failed"));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setUrl("");
    setName("");
    setEmail("");
  };

  return (
    <section
      id="audit"
      data-testid="audit-section"
      className="relative py-24 md:py-36 bg-[#FFFFFF] border-y border-[#E5E3DB]"
    >
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-7">
            <div className="eyebrow text-ink mb-4">
              {t("audit.overline")} · 03
            </div>
            <h2 className="heading-display text-5xl md:text-6xl lg:text-7xl" data-testid="audit-title">
              {t("audit.title")}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 self-end">
            <p className="text-base md:text-lg text-ink leading-relaxed">
              {t("audit.sub")}
            </p>
          </div>
        </div>

        {!result && !loading && (
          <form
            data-testid="audit-form"
            onSubmit={submit}
            className="border border-navy bg-[#F7F5F0] p-6 md:p-10"
          >
            <label htmlFor="audit-url" className="eyebrow text-ink">
              URL
            </label>
            <div className="flex flex-col md:flex-row items-stretch gap-3 mt-2 mb-6">
              <input
                id="audit-url"
                data-testid="audit-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={t("audit.placeholder")}
                required
                className="flex-1 bg-transparent border-0 border-b border-navy pb-4 font-serif text-3xl md:text-5xl tracking-tight placeholder:text-navy/30 focus:outline-none focus:border-navy"
              />
              <button
                type="submit"
                data-testid="audit-submit-btn"
                className="btn-editorial btn-editorial-primary self-start md:self-end"
              >
                <Sparkles size={16} />
                {t("audit.btn")}
                <ArrowRight size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="flex flex-col">
                <label htmlFor="audit-name" className="eyebrow text-ink mb-1">
                  {t("audit.name_label")}
                </label>
                <input
                  id="audit-name"
                  data-testid="audit-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-transparent border-b border-navy/30 pb-3 pt-2 min-h-[44px] text-base focus:outline-none focus:border-navy"
                />
              </div>
              <div className="flex flex-col">
                <label htmlFor="audit-email" className="eyebrow text-ink mb-1">
                  {t("audit.email_label")}
                </label>
                <input
                  id="audit-email"
                  data-testid="audit-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-transparent border-b border-navy/30 pb-3 pt-2 min-h-[44px] text-base focus:outline-none focus:border-navy"
                />
              </div>
            </div>
          </form>
        )}

        {loading && (
          <div
            data-testid="audit-loading"
            className="border border-navy bg-navy text-[#F7F5F0] p-10 md:p-14 min-h-[340px]"
          >
            <div className="eyebrow text-white/60 mb-8">{t("audit.result.working")}</div>
            <div className="heading-display text-3xl md:text-5xl mb-10">
              {url}
            </div>
            <ul className="space-y-4">
              {steps.map((s, i) => (
                <li
                  key={i}
                  className={`flex items-center gap-3 transition-all duration-500 ${
                    i <= stepIdx ? "opacity-100" : "opacity-30"
                  }`}
                >
                  <span
                    className={`w-5 h-5 shrink-0 rounded-full border ${
                      i < stepIdx
                        ? "bg-[#F7F5F0] border-[#F7F5F0]"
                        : i === stepIdx
                        ? "border-[#F7F5F0] animate-pulse"
                        : "border-white/30"
                    }`}
                  />
                  <span className="text-base md:text-lg">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {result?.status === "niet_te_lezen" && (
          <div ref={resultRef} data-testid="audit-unreadable" className="scroll-mt-24 border border-navy bg-[#F7F5F0] p-8 md:p-12">
            <div className="eyebrow text-ink mb-3 break-all">{result.url}</div>
            <h3 className="heading-display text-3xl md:text-4xl mb-4">{t("audit.result.unreadable_title")}</h3>
            <p data-testid="audit-unreadable-message" className="text-base md:text-lg text-ink max-w-2xl leading-relaxed mb-8">{result.message}</p>
            {result.scores?.performance != null && (
              <div data-testid="audit-unreadable-speed" className="border-t border-navy/15 pt-8 mb-10 flex flex-col sm:flex-row sm:items-center gap-8">
                <ScoreRing t={t} testid="performance" label={t("audit.result.performance")} value={result.scores.performance} measured />
                <div className="order-first sm:order-none">
                  <h4 className="heading-display text-2xl">{t("audit.result.speed_only")}</h4>
                  <SpeedLine m={result.metingen} lang={lang} />
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-3">
              <BookCall t={t} />
              <button type="button" onClick={reset} data-testid="audit-reset-btn" className="btn-editorial btn-editorial-outline">
                <RotateCcw size={16} /> {t("audit.result.new")}
              </button>
            </div>
          </div>
        )}

        {result && result.status !== "niet_te_lezen" && (
          <div ref={resultRef} data-testid="audit-result" className="scroll-mt-24 space-y-12">
            {/* Score row */}
            <div className="border border-navy p-8 md:p-12 bg-[#F7F5F0]">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6 justify-items-center">
                <ScoreRing t={t} testid="overall" label={t("audit.result.overall")} value={result.scores.overall} measured />
                <ScoreRing t={t} testid="seo" label={t("audit.result.seo")} value={result.scores.seo} measured />
                <ScoreRing t={t} testid="aeo" label={t("audit.result.aeo")} value={result.scores.aeo} measured />
                <ScoreRing t={t} testid="performance" label={t("audit.result.performance")} value={result.scores.performance} measured={!!result.gemeten?.pagespeed} />
              </div>
              <SpeedLine m={result.metingen} lang={lang} />
              <div className="mt-10 max-w-3xl">
                <div className="eyebrow text-ink mb-3">{result.url}</div>
                <p className="font-serif text-2xl md:text-3xl leading-snug text-navy">
                  {result.summary}
                </p>
              </div>
            </div>

            {/* Findings */}
            <div className="grid grid-cols-12 gap-y-8 md:gap-x-8">
              <div className="col-span-12 md:col-span-7">
                <h3 className="heading-display text-3xl md:text-4xl mb-6">
                  {t("audit.result.findings")}
                </h3>
                <ul className="divide-y divide-[#E5E3DB] border-y border-[#E5E3DB]">
                  {result.findings.map((f, i) => (
                    <li key={i} className="py-5 flex gap-4 items-start" data-testid={`finding-${i}`}>
                      <SeverityIcon s={f.severity} />
                      <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-medium">{f.title}</span>
                          <span className="eyebrow text-ink">
                            {sev[f.severity] || f.severity}
                          </span>
                        </div>
                        <p className="text-sm text-ink mt-2 leading-relaxed">
                          {f.description}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="col-span-12 md:col-span-5 space-y-10">
                <div>
                  <h3 className="heading-display text-3xl md:text-4xl mb-6">
                    {t("audit.result.recommendations")}
                  </h3>
                  <ol className="space-y-4 list-none counter-reset-[rec]">
                    {result.recommendations.map((r, i) => (
                      <li key={i} className="flex gap-4">
                        <span className="font-serif text-xl text-ink w-8">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="text-sm text-navy leading-relaxed">{r}</span>
                      </li>
                    ))}
                  </ol>
                </div>
                <div className="bg-navy text-[#F7F5F0] p-6">
                  <div className="eyebrow text-white/60 mb-3">
                    {t("audit.result.aeo_ops")}
                  </div>
                  <ul className="space-y-3">
                    {result.aeo_opportunities.map((a, i) => (
                      <li key={i} className="flex gap-3 text-sm leading-relaxed">
                        <Sparkles size={14} className="mt-1 flex-shrink-0 text-white/70" />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-4">
              <BookCall t={t} />
              <button
                type="button"
                onClick={reset}
                data-testid="audit-reset-btn"
                className="btn-editorial btn-editorial-outline"
              >
                <RotateCcw size={16} /> {t("audit.result.new")}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
